import type { google } from '@alugha/ima';
import { isNil } from 'ramda';
import { TrackingScheme } from 'tracking';
import videojs, { ContribAds, VideoJsPlayer } from 'video.js';

import {
  AdEventType,
  Event,
  LabelKey,
  LegacyTrackingEvent,
} from '@src/types/event';

import BaseNormalizer from './BaseNormalizer';

export const PLUGIN_NAME = 'eventManagerAdsNormalizer';

type AdType = ContribAds['adType'];
interface State {
  latestAdType: null | AdType;
  startVideoAdTime: null | number;
  numAdStarted: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

interface AdsNormalizer {
  setState: (state: Partial<State>) => void;
}
class AdsNormalizer extends BaseNormalizer {
  static defaultState: State;

  vjsPlayer: VideoJsPlayer;
  imaAdsManager?: google.ima.AdsManager;
  imaAdsLoader?: google.ima.AdsLoader;

  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);
    this.vjsPlayer = player;

    this.setupImaListeners();
    this.setupContribAdsListeners();
    this.setupResetStateListeners();
  }

  private setupContribAdsListeners() {
    this.vjsPlayer.on(Event.ADTIMEOUT, this.handleAdTimeout);
  }

  private handleAdTimeout = (/* evt: videojs.EventTarget.Event */) => {
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData(),
      [LabelKey.EVENT_TYPE]: AdEventType.LOADVIDEOADTIMEOUT,
    });
  };

  private disposeContribAdsListeners() {
    this.vjsPlayer.off(Event.ADTIMEOUT, this.handleAdTimeout);
  }

  private setupImaListeners() {
    this.vjsPlayer.on(Event.ADS_LOADER, this.handleImaAdsLoaderLoaded);
    this.vjsPlayer.on(Event.ADS_MANAGER, this.handleImaAdsManagerLoaded);
  }

  private handleImaAdsLoaderLoaded = (evt: videojs.EventTarget.Event) => {
    this.imaAdsLoader = evt.adsLoader;
    this.imaAdsLoader?.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );
  };

  private handleImaAdsManagerLoaded = (evt: videojs.EventTarget.Event) => {
    this.imaAdsManager = evt.adsManager;

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.handleContentPauseRequested
    );

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.SKIPPED,
      this.handleSkippedAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.USER_CLOSE,
      this.handleUserCloseAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.CLICK,
      this.handleClickAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.IMPRESSION,
      this.handleImpressionAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.MIDPOINT,
      this.handleMidpointAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.COMPLETE,
      this.handleCompleteAd
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.handleAllAdsCompleted
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.VOLUME_CHANGED,
      this.handleAdVolumeChanged
    );
    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.VOLUME_MUTED,
      this.handleAdVolumeMuted
    );
  };

  private disposeImaListeners() {
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.handleContentPauseRequested
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.SKIPPED,
      this.handleSkippedAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.USER_CLOSE,
      this.handleUserCloseAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.CLICK,
      this.handleClickAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.IMPRESSION,
      this.handleImpressionAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.MIDPOINT,
      this.handleMidpointAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.COMPLETE,
      this.handleCompleteAd
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.handleAllAdsCompleted
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.VOLUME_CHANGED,
      this.handleAdVolumeChanged
    );
    this.imaAdsManager?.removeEventListener(
      window.google.ima.AdEvent.Type.VOLUME_MUTED,
      this.handleAdVolumeMuted
    );

    this.vjsPlayer.off(Event.ADS_MANAGER, this.handleImaAdsManagerLoaded);

    this.imaAdsLoader?.removeEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );

    this.vjsPlayer.off(Event.ADS_LOADER, this.handleImaAdsLoaderLoaded);
  }

  private getSecondsFromVideoAdStarted() {
    return this.state.startVideoAdTime
      ? Math.round((Date.now() - this.state.startVideoAdTime) / 1000)
      : null;
  }

  private handleContentPauseRequested = (adEvent: google.ima.AdEvent) => {
    this.vjsPlayer.trigger(Event.CONTENT_PAUSE_REQUESTED);
    this.setState({
      startVideoAdTime: Date.now(),
      numAdStarted: this.state.numAdStarted + 1,
    });

    this.trigger(
      this.vjsPlayer.options_.trackingScheme === TrackingScheme.V3
        ? LegacyTrackingEvent.STARTVIDEOAD
        : Event.AD_EVENT,
      {
        ...this.createCommonEventData({ adEvent }),
        [LabelKey.EVENT_TYPE]: AdEventType.STARTVIDEOAD,
      }
    );
  };

  private handleContentResumeRequested = (adEvent: google.ima.AdEvent) => {
    this.vjsPlayer.trigger(Event.CONTENT_RESUME_REQUESTED);
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      [LabelKey.EVENT_TYPE]: AdEventType.ENDVIDEOAD,
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };

  private handleSkippedAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };

  private handleUserCloseAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };

  private handleClickAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };
  private handleImpressionAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };
  private handleMidpointAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    });
  };
  private handleCompleteAd = (adEvent: google.ima.AdEvent) => {
    const secondsFromVideoAdStarted = this.getSecondsFromVideoAdStarted();
    const eventData = {
      ...this.createCommonEventData({ adEvent }),
      ...(!isNil(secondsFromVideoAdStarted) && {
        [LabelKey.AD_SEC_FROM_START]: secondsFromVideoAdStarted,
      }),
    };
    this.trigger(Event.AD_EVENT, eventData);

    this.vjsPlayer.contextPlugin.addAdTypeCompleted(eventData[LabelKey.ADTYPE]);
  };

  private handleAllAdsCompleted = (adEvent: google.ima.AdEvent) => {
    this.trigger(Event.AD_EVENT, {
      ...this.createCommonEventData({ adEvent }),
      [LabelKey.NUM_AD_STARTED]: this.state.numAdStarted,
    });
    this.setState({ latestAdType: null });
    this.vjsPlayer.contextPlugin.setCompleteAllAds(true);
  };

  private handleAdError = (adErrorEvent: google.ima.AdErrorEvent) => {
    this.trigger(
      Event.AD_EVENT,
      this.createCommonAdErrorEventData({ adErrorEvent })
    );
  };

  protected createCommonAdErrorEventData(payload: {
    adErrorEvent: google.ima.AdErrorEvent;
  }) {
    const adError = payload.adErrorEvent?.getError?.();
    const innerError = adError.getInnerError() ?? null;
    return {
      ...super.createCommonEventData(),
      [LabelKey.EVENT_TYPE]: AdEventType.VIDEOADERROR,
      [LabelKey.AD_ERROR_CODE]: adError.getErrorCode(),
      [LabelKey.AD_INNER_ERROR]: innerError
        ? JSON.stringify({
            name: innerError.name,
            message: innerError.message,
          })
        : null,
      [LabelKey.AD_ERROR_MESSAGE]: adError.getMessage(),
      [LabelKey.AD_ERROR_TYPE]: adError.getType(),
      [LabelKey.AD_VAST_ERROR_CODE]: adError.getVastErrorCode(),
    };
  }

  protected createCommonEventData(payload?: {
    adEvent?: google.ima.AdEvent & { type?: google.ima.AdEvent.Type };
  }) {
    const currentAd = payload?.adEvent?.getAd?.();

    this.setState({
      latestAdType: this.vjsPlayer.ads.adType || null,
    });

    return {
      ...super.createCommonEventData(),
      [LabelKey.ADTYPE]: this.state.latestAdType,
      ...(payload?.adEvent?.type && {
        [LabelKey.EVENT_TYPE]: payload.adEvent.type,
      }),
      ...(currentAd && { adID: currentAd.getAdId() }),
    };
  }

  protected handleAdVolumeChanged = () => {
    this.vjsPlayer.contextPlugin.addAdVolumeChangeCount();
  };

  protected handleAdVolumeMuted = () => {
    this.vjsPlayer.contextPlugin.addAdVolumeChangeCount();
  };

  private setupResetStateListeners() {
    this.vjsPlayer.on(
      [Event.SOURCESET, Event.CONTENTCHANGED, Event.ENDED],
      this.handleResetState
    );
  }

  private disposeResetStateListeners() {
    this.vjsPlayer.off(
      [Event.SOURCESET, Event.CONTENTCHANGED, Event.ENDED],
      this.handleResetState
    );
  }

  private handleResetState = (/* evt: videojs.EventTarget.Event */) => {
    this.setState(AdsNormalizer.defaultState);
  };

  dispose() {
    this.disposeResetStateListeners();
    this.disposeImaListeners();
    this.disposeContribAdsListeners();
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, AdsNormalizer);

export type AdsNormalizerFactoryFn = (options?: Options) => AdsNormalizer;

AdsNormalizer.defaultState = {
  latestAdType: null,
  startVideoAdTime: null,
  numAdStarted: 0,
};

export default AdsNormalizer;
